import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import apiClient from '@/shared/services/apiClient';
import { getPopularPosts } from '@/features/blog/services/blogApi';
import type { Tag, Category, Post } from '@/shared/types/api';

export default function Sidebar() {
  const { data: tags } = useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await apiClient.get('/blog/tags');
      return res.data.tags;
    },
  });

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await apiClient.get('/blog/categories');
      return res.data.categories;
    },
  });

  const { data: popularPosts } = useQuery<(Post & { views?: number })[]>({
    queryKey: ['popularPosts'],
    queryFn: () => getPopularPosts(5),
  });

  return (
    <aside>
      <hr />

      <div className="mb-6">
        <h3 className="text-sm font-bold mb-2 text-accent">About</h3>
        <p className="text-sm text-muted leading-relaxed">
          Senior iOS Developer and Team Lead with {new Date().getFullYear() - 2014}+ years of experience.
          Writing about software development, architecture, and technology.
        </p>
      </div>

      {categories && categories.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold mb-2 text-accent">Categories</h3>
          <div className="text-sm">
            {categories.map((cat, i) => (
              <span key={cat.id}>
                {i > 0 && ' '}
                <Link to={`/category/${cat.id}/${cat.slug}`} className="text-accent hover:underline">{cat.name}</Link>
                <span className="text-muted"> ({cat.post_count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {tags && tags.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold mb-2 text-accent">Tags</h3>
          <div className="text-sm">
            {tags.map((tag, i) => (
              <span key={tag.id}>
                {i > 0 && ' '}
                <Link to={`/tag/${tag.id}/${tag.slug}`} className="text-accent hover:underline">#{tag.name}</Link>
                <span className="text-muted"> ({tag.post_count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {popularPosts && popularPosts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-bold mb-2 text-accent">[ POPULAR ]</h3>
          <ul className="text-sm list-none p-0 m-0 space-y-1">
            {popularPosts.map((p) => (
              <li key={p.id} className="flex items-start gap-1">
                <Link to={`/blog/${p.id}/${p.slug}`} className="text-accent hover:underline flex-1">
                  {p.title}
                </Link>
                {p.views != null && (
                  <span className="text-xs text-muted flex items-center gap-0.5 whitespace-nowrap">
                    <Eye size={10} />{p.views}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-sm font-bold mb-2 text-accent">Links</h3>
        <ul className="text-sm list-none p-0 m-0 space-y-0.5">
          <li><a href="https://github.com/MysticalStorm" target="_blank" rel="noopener noreferrer">GitHub</a></li>
          <li><a href="https://www.linkedin.com/in/cv-deadline/" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
        </ul>
      </div>

    </aside>
  );
}
